import React, { useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { ServiceClientContext } from './serviceClientContext';
import serviceClientReducer from './serviceClientReducer';
import { DEFAULT_SERVICE_CLIENT_STATE } from './constants/defaultState';
import { clearState, setLoading } from '../utils';
import { IServiceClientState } from './types';
import { TCreateVehicleFormikValues } from 'src/views/serviceClient/components/types';
import { TCreateServiceClientVehicleServiceHistoryPayload } from './types';
import { SnackbarAlert } from 'src/components/Alerts/components/System/components/SnackbarAlert';
import {
    CreateServiceClientVehicle,
    createServiceClient,
    upsertServiceClientFromSoldLead,
    getServiceClientById,
    searchServiceClientsByFilters,
    updateServiceClientVehicle,
    updateServiceClient,
    createServiceClientVehicleServiceHistory,
    deleteServiceClientVehicleServiceHistory,
    sendTemplateServiceClient,
} from './utils';
import { TCurrentTemplate } from 'src/modules/conversations/components/modalTemplates/types';
import type { TServiceClientFromSoldLeadPayload } from 'src/modules/serviceClients/helpers/mapLeadToServiceClientFromSoldPayload';

export const ServiceClientState = ({ children }: IServiceClientState) => {
    const [state, dispatch] = useReducer(
        serviceClientReducer,
        DEFAULT_SERVICE_CLIENT_STATE
    );
    const { t } = useTranslation();
    const { customAlert } = SnackbarAlert();

    return (
        <ServiceClientContext.Provider
            value={{
                ...state,
                clearState: () =>
                    clearState(dispatch, {
                        clients: null,
                        client: null,
                        total: 0,
                        loading: false,
                        error: null,
                        success: null,
                    }),
                setLoading: () => setLoading(dispatch),
                searchServiceClientsByFilters: (filters, options) =>
                    searchServiceClientsByFilters(dispatch, filters, options),
                createServiceClientVehicle: (
                    clientServiceId: string,
                    values: TCreateVehicleFormikValues
                ) =>
                    CreateServiceClientVehicle(
                        dispatch,
                        clientServiceId,
                        values
                    ),
                updateServiceClientVehicle: (
                    clientServiceId: string,
                    vehicleId: string,
                    values: TCreateVehicleFormikValues
                ) =>
                    updateServiceClientVehicle(
                        dispatch,
                        clientServiceId,
                        vehicleId,
                        values
                    ),
                createServiceClientVehicleServiceHistory: (
                    clientServiceId: string,
                    vehicleId: string,
                    payload: TCreateServiceClientVehicleServiceHistoryPayload
                ) =>
                    createServiceClientVehicleServiceHistory(
                        dispatch,
                        clientServiceId,
                        vehicleId,
                        payload,
                        { t, customAlert }
                    ),
                deleteServiceClientVehicleServiceHistory: (
                    clientServiceId: string,
                    vehicleId: string,
                    serviceHistoryId: string
                ) =>
                    deleteServiceClientVehicleServiceHistory(
                        dispatch,
                        clientServiceId,
                        vehicleId,
                        serviceHistoryId,
                        { t, customAlert }
                    ),
                createServiceClient: (body) =>
                    createServiceClient(dispatch, body),
                upsertServiceClientFromSoldLead: (
                    payload: TServiceClientFromSoldLeadPayload
                ) => upsertServiceClientFromSoldLead(dispatch, payload),
                updateServiceClient: (
                    serviceClientId: string,
                    values: Record<string, unknown>,
                    options?: { playSound?: boolean }
                ) =>
                    updateServiceClient(
                        dispatch,
                        serviceClientId,
                        values,
                        { t, customAlert },
                        options
                    ),
                getServiceClientById: (serviceClientId: string) =>
                    getServiceClientById(dispatch, serviceClientId),
                sendTemplateServiceClient: (
                    template: TCurrentTemplate,
                    serviceClientId: string,
                    options?: { playSound?: boolean }
                ) =>
                    sendTemplateServiceClient(
                        dispatch,
                        template,
                        serviceClientId,
                        { t, customAlert },
                        options
                    ),
            }}
        >
            {children}
        </ServiceClientContext.Provider>
    );
};
